import React, { useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  IconButton,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { VirtualProductQuoteRequest, WindowModel, WindowModelField } from '../../../models/virtualProduct/types';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface StepThreeProps {
  form: VirtualProductQuoteRequest;
  setForm: React.Dispatch<React.SetStateAction<VirtualProductQuoteRequest>>;
  onSubmit: (WindowModels?: WindowModel[]) => void;
}

const WINDOW_TYPE = 'WindowType';

const brandKeyMap: Record<string, string> = {
  'Harvey Windows + Doors': 'harvey',
  'Andersen': 'andersen',
  'Simonton': 'simonton',
  'MI Windows & Doors': 'miwindows',
  'Other': 'default',
};

const brandWindowTypesMap: Record<string, string[]> = {
  harvey: [
    'Double Hung', 'Single Hung',
    'Casement', 'Awning',
    'Gliding', 'Picture',
    'Bay', 'Bow',
    'Garden', 'Rolling',
    'Hopper', 'Horizontal Slider'
  ],

  miwindows: [
    'Double Hung', 'Single Hung',
    'Casement', 'Awning',
    'Picture', 'Specialty',
    'Horizontal Slider'
  ],
  andersen: [
    'Double Hung', 'Awning', 'Bay',
    'Bow', 'Casement', 'Single Hung',
    'Gliding', 'Picture', 'Specialty'
  ],
  simonton: [
    'Double Hung', 'Casement',
    'Awning', 'Picture',
    'Specialty', 'Sliding',
    'Bay', 'Bow',
    'Garden', 'Combination'
  ],
  default: [
    'Double Hung', 'Single Hung',
    'Casement', 'Awning',
    'Gliding', 'Picture',
    'Specialty', 'Custom Shape',
    'Sliding', 'Bay',
    'Bow', 'Garden',
    'Rolling', 'Hopper',
    'Combination', 'Horizontal Slider',
    'Customer/Other'
  ]
};
const defaultWindowForType = (type: string): WindowModel => ({
  WindowType: type,
  Quantity: 1,
  Width: '',
  Height: '',
  SizeType: 'Rough',
  Preferences: '',
});

const StepThree: React.FC<StepThreeProps> = ({ form, setForm, onSubmit }) => {
  const brandKey = brandKeyMap[form.Brand ?? ''] ?? 'default';
  const availableWindowTypes = brandWindowTypesMap[brandKey];
  const windows: WindowModel[] = form.WindowModels?.filter(w =>
    availableWindowTypes.includes(w.WindowType) || w.WindowType === ''
  ) ?? [];

useEffect(() => {
  const selectedTypes = new Set(form.WindowTypes || []);
  
  setForm(prev => {
    const currentWindows = prev.WindowModels || [];
    const updatedWindows = currentWindows.filter(window => 
      selectedTypes.has(window.WindowType) || window.WindowType === ''
    );
    
    const existingTypes = new Set(updatedWindows.map(window => window.WindowType));
    const missingDefaults = (form.WindowTypes || [])
      .filter(type => !existingTypes.has(type))
      .map(type => defaultWindowForType(type));

    const nextWindows = [...updatedWindows, ...missingDefaults];

    if (JSON.stringify(nextWindows.map(window => window.WindowType)) !== JSON.stringify(currentWindows.map(window => window.WindowType))) {
      return { ...prev, WindowModels: nextWindows };
    }
    
    return prev;
  });
}, [form.WindowTypes, setForm]);

const handleWindowChange = <K extends WindowModelField>(
  index: number,
  field: K,
  value: WindowModel[K]
) => {
  setForm(prev => {
    const list = [...(prev.WindowModels ?? [])];
    const oldWindowType = list[index].WindowType;
    const newWindowType = field === WINDOW_TYPE ? (value as string) : oldWindowType;

    list[index] = { ...list[index], [field]: value };
    let updatedWindowTypes = [...(prev.WindowTypes ?? [])];

    if (field === WINDOW_TYPE && newWindowType !== oldWindowType) {
      if (oldWindowType && list.filter(window => window.WindowType === oldWindowType).length === 0) {
        updatedWindowTypes = updatedWindowTypes.filter(type => type !== oldWindowType);
      }

      if (newWindowType && !updatedWindowTypes.includes(newWindowType)) {
        updatedWindowTypes = [...updatedWindowTypes, newWindowType];
      }
    }

    return { ...prev, WindowModels: list, WindowTypes: updatedWindowTypes };
  });
};

  const addWindow = () => {
    setForm(prev => ({
      ...prev,
      WindowModels: [
        ...(prev.WindowModels ?? []),
        {
          WindowType: '',
          Quantity: 1,
          Width: '',
          Height: '',
          SizeType: 'Rough',
          Preferences: '',
        },
      ],
    }));
  };

const removeWindow = (index: number) => {
  setForm(prev => {
    const list = [...(prev.WindowModels ?? [])];
    const windowToRemove = list[index];
    const updatedWindowModels = list.filter((_, i) => i !== index);

    const windowTypeToRemove = windowToRemove?.WindowType;
    const isLastOfType = updatedWindowModels.filter(w => w.WindowType === windowTypeToRemove).length === 0;

    let updatedWindowTypes = prev.WindowTypes;
    
    if (isLastOfType && windowTypeToRemove !== '') {
      updatedWindowTypes = (prev.WindowTypes ?? []).filter(type => type !== windowTypeToRemove);
    }

    return { 
      ...prev, 
      WindowModels: updatedWindowModels,
      WindowTypes: updatedWindowTypes
    };
  });
};

  const handleSubmitStep = () => {
    onSubmit();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setForm((prev) => ({
        ...prev,
        Files: [...(prev.Files || []), ...files],
      }));
    }
  };

  const handleRemoveFile = (index: number) => {
    setForm((prev) => ({
      ...prev,
      Files: prev.Files?.filter((_, i) => i !== index)
    }));
  };


  return (
    <Box className='vp-container'>
      <Grid
        container
        columnSpacing={4}
        rowSpacing={2}
        alignItems="start"
        sx={{
          width: '100%',
          backgroundColor: '#e2e2e2',
          borderRadius: 2,
          px: 3,
          py: 4,
        }}
      >
        {windows.map((window, index) => (
          <Grid
            container
            key={`window-${index}`}
            columnSpacing={2}
            rowSpacing={2}
            alignItems="start"
          >
            <Grid item xs={12} sm={4}>
              <label className="form-label" htmlFor={`WindowType-${index}`}>
                {index + 1}. Window Type
              </label>
              <TextField
                select
                id={`WindowType-${index}`}
                value={window.WindowType}
                onChange={(e) =>
                  handleWindowChange(index, 'WindowType', e.target.value)
                }
                fullWidth
                size="small"
                className="form-input"
              >
                {availableWindowTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={2}>
              <label className="form-label" htmlFor={`Quantity-${index}`}>
                QTY
              </label>
              <TextField
                id={`Quantity-${index}`}
                type="number"
                value={window.Quantity}
                onChange={(e) =>
                  handleWindowChange(index, 'Quantity', Number(e.target.value))
                }
                size="small"
                className="form-input"
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <label className="form-label" htmlFor={`Preferences-${index}`} style={{ display: 'flex', alignItems: 'center' }}>
                Key Features / Preferences
                <Tooltip title="Please elaborate on design options such as color, hardware, glass, grill, screen types, or other preferences for each window." arrow>
                  <InfoOutlinedIcon fontSize="small" sx={{ ml: 1, cursor: 'pointer', color: '#1976d2' }} />
                </Tooltip>
              </label>
              <TextField
                id={`Preferences-${index}`}
                multiline
                minRows={4}
                value={window.Preferences}
                onChange={(e) =>
                  handleWindowChange(index, 'Preferences', e.target.value)
                }
                fullWidth
                size="small"
                className="form-input"
              />
            </Grid>

            <Grid item sm={1}>
              <IconButton onClick={() => removeWindow(index)}>
                <DeleteIcon />
              </IconButton>
            </Grid>

            <Grid item xs={12}>
              <label className="form-label">Size</label>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <TextField
                    id={`Width-${index}`}
                    value={window.Width}
                    onChange={(e) =>
                      handleWindowChange(index, 'Width', e.target.value)
                    }
                    fullWidth
                    size="small"
                    className="form-input"
                    type='number'
                    placeholder="Width"
                    inputProps={{ step: 0.01, min: 0 }}
                  />
                </Grid>

                <Grid item xs={2}>
                  <TextField
                    id={`Height-${index}`}
                    value={window.Height}
                    onChange={(e) =>
                      handleWindowChange(index, 'Height', e.target.value)
                    }
                    fullWidth
                    size="small"
                    className="form-input"
                    type='number'
                    placeholder="Height"
                    inputProps={{ step: 0.01, min: 0 }}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={6}>
              <RadioGroup
                row
                value={window.SizeType}
                onChange={(e) =>
                  handleWindowChange(index, 'SizeType', e.target.value as 'Rough' | 'Exact')
                }
              >
                <FormControlLabel
                  value="Rough"
                  control={<Radio size="small" />}
                  label={<span className="form-label-text">Rough Opening</span>}
                />
                <FormControlLabel
                  value="Exact"
                  control={<Radio size="small" />}
                  label={<span className="form-label-text">Exact/Make Size (TTT)</span>}
                />
              </RadioGroup>
            </Grid>

            {index < windows.length - 1 && (
              <Grid item xs={12}>
                <Box sx={{ borderBottom: '1px solid #767676', marginBottom: '16px' }} />
              </Grid>
            )}
          </Grid>
        ))}

        <Grid container style={{ marginTop: "15px" }}>
          <Button startIcon={<AddIcon />} onClick={addWindow}>
            Add Another Window
          </Button>
          <Grid item xs={12}>
            <Box
              sx={{ borderBottom: '1px solid #767676', marginY: 2, width: '100%' }}
            />
          </Grid>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  border: '1px dashed #ccc',
                  padding: 3,
                  textAlign: 'center',
                  backgroundColor: '#fff',
                }}
              >
                <input
                  type="file"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg,.bmp"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="upload-files"
                />
                <Box
                  component="label"
                  htmlFor="upload-files"
                  sx={{
                    display: 'flex',
                    cursor: 'pointer',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '10px'
                    }}
                  >
                    <UploadFileIcon color="primary" />
                  </Box>

                  <Typography className='file-input'>
                    <Typography className='file-input-bold' component="span">
                      Click to Upload
                    </Typography>{' '}
                    photos or documents
                  </Typography>
                  <Typography className='file-input-size' variant="caption">(Max file size: 25 MB)</Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={8}>
              <Box
                sx={{
                  display: 'flex',
                  overflowX: 'auto',
                  gap: 2,
                  pb: 1,
                }}
                className="uploaded-files"
              >
                {form.Files?.map((file, idx) => {
                  const url = URL.createObjectURL(file);
                  const sizeKB = (file.size / 1024).toFixed(2);
                  const fileName = file.name;

                  return (
                    <Box
                      key={idx}
                      className="upload-file"
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        backgroundColor: '#fff',
                        border: '1px solid #CACACA',
                        padding: 1,
                        borderRadius: 2,
                        minWidth: 200,
                        flexShrink: 0,
                      }}
                    >
                      <DescriptionIcon color="action" />

                      <Box className="file-settings" sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                        <Typography
                          className="file-name"
                          fontWeight={600}
                          noWrap
                          sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {fileName}
                        </Typography>
                        <Typography className="file-size" variant="caption" color="text.secondary">
                          {sizeKB} KB
                        </Typography>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer">
                          Click to view
                        </a>
                      </Box>

                      <IconButton sx={{ padding: 0 }} onClick={() => handleRemoveFile(idx)}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Button className="blueButtonOutline submit-button-blue" onClick={handleSubmitStep}>
        Submit
      </Button>
    </Box>
  );
};

export default StepThree;
